/**
 * Tiny imperative DOM helper. This is the entire "component model" for this
 * app: plain functions/classes that create and return DOM nodes. No JSX, no
 * virtual DOM, no diffing — direct DOM construction and mutation.
 */

type EventHandlers = {
  [K in keyof HTMLElementEventMap as `on${K}`]?: (ev: HTMLElementEventMap[K]) => void;
};

export type ElAttrs = Partial<{
  className: string;
  id: string;
  title: string;
  href: string;
  type: string;
  value: string;
  placeholder: string;
  name: string;
  role: string;
  tabIndex: number | string;
  disabled: boolean;
  checked: boolean;
  colSpan: number | string;
  dataset: Record<string, string>;
  style: Partial<CSSStyleDeclaration>;
  html: string;
}> &
  EventHandlers &
  Record<string, unknown>;

export type ElChild = Node | string | number | null | undefined | false;

/** Create a DOM element with attributes/handlers and append children. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: ElAttrs | null,
  children?: ElChild[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  applyAttrs(node, attrs);
  if (children) appendChildren(node, children);
  return node;
}

export function applyAttrs(node: HTMLElement, attrs?: ElAttrs | null): void {
  if (!attrs) return;
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null) continue;

    if (key === "className") {
      node.className = value as string;
      continue;
    }
    if (key === "dataset") {
      Object.assign(node.dataset, value as Record<string, string>);
      continue;
    }
    if (key === "style") {
      Object.assign(node.style, value as Partial<CSSStyleDeclaration>);
      continue;
    }
    if (key === "html") {
      node.innerHTML = value as string;
      continue;
    }
    if (key.startsWith("on") && typeof value === "function") {
      const eventName = key.slice(2).toLowerCase();
      node.addEventListener(eventName, value as EventListener);
      continue;
    }
    if (typeof value === "boolean") {
      if (value) node.setAttribute(key, "");
      else node.removeAttribute(key);
      // Keep DOM property in sync for form controls (disabled/checked).
      (node as unknown as Record<string, unknown>)[key] = value;
      continue;
    }
    node.setAttribute(key, String(value));
  }
}

export function appendChildren(node: Element, children: ElChild[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === "string" || typeof child === "number" ? String(child) : child);
  }
}

/** Replace all children of a container with a single node (or clear it). */
export function mount(container: Element, node?: Node | null): void {
  container.replaceChildren();
  if (node) container.append(node);
}

export function clear(container: Element): void {
  container.replaceChildren();
}

export function fragment(children: ElChild[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  appendChildren(frag as unknown as Element, children);
  return frag;
}
